import { useMemo, useState } from "react";
import { Check, ChevronDown, LibraryBig } from "lucide-react";
import { GenieAvatar } from "@/components/GenieAvatar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import type { AgentSelectionProps } from "@/types/knowledge/knowledge-acquisition";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const AgentSelection = ({ agents, selectedAgentId, onAgentSelect }: AgentSelectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedAgent = useMemo(
    () => agents.find((agent) => agent.id === selectedAgentId),
    [agents, selectedAgentId],
  );
  const getBrandColor = (agent: (typeof agents)[number]) =>
    agent.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
  const hasCustomBranding = (agent: (typeof agents)[number]) =>
    Boolean(
      agent.branding &&
        (agent.branding.primaryColor !== DEFAULT_BRANDING.primaryColor ||
          agent.branding.secondaryColor ||
          agent.branding.customAvatarUrl ||
          agent.branding.gradientEnabled),
    );

  const handleSelect = (agentId: string) => {
    onAgentSelect(agentId);
    setIsOpen(false);
  };

  return (
    <div className="mb-5 rounded-xl border border-genie-border bg-gray-50/50 p-4">
      <div className="mb-3 flex min-w-0 items-start gap-3">
        <GenieIcon name="genie-lamp" size={28} className="h-7 w-7 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">Where should this knowledge go?</p>
          <p className="mt-0.5 text-xs text-gray-500">
            {selectedAgent
              ? `It will be added to your library and connected to ${selectedAgent.name}.`
              : "It will be saved to your shared library. You can connect it to one or more Genies afterwards."}
          </p>
        </div>
      </div>

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex min-h-12 w-full items-center gap-3 rounded-xl border bg-white px-3 text-left transition-all hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-genie-primary"
            style={{
              borderColor: selectedAgent ? `${getBrandColor(selectedAgent)}66` : "#E5EAF0",
            }}
            aria-label={`Knowledge destination: ${selectedAgent?.name || "Shared library only"}`}
          >
            {selectedAgent ? (
              <span className="flex h-8 w-8 shrink-0 overflow-hidden rounded-full">
                <GenieAvatar
                  size={32}
                  color={getBrandColor(selectedAgent)}
                  secondaryColor={selectedAgent.branding?.secondaryColor}
                  gradientEnabled={selectedAgent.branding?.gradientEnabled}
                  gradientAngle={selectedAgent.branding?.gradientAngle}
                  customAvatarUrl={selectedAgent.branding?.logoUrl || selectedAgent.branding?.customAvatarUrl}
                  hasCustomBranding={hasCustomBranding(selectedAgent)}
                />
              </span>
            ) : (
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#4E9CFF] to-[#FF4DB8] text-white">
                <LibraryBig className="h-4 w-4" />
              </span>
            )}
            <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[#222B45]">
              {selectedAgent?.name || "Shared library only"}
            </span>
            <ChevronDown className={cn("h-4 w-4 shrink-0 text-gray-400 transition-transform", isOpen && "rotate-180")} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={6} className="w-[min(26rem,calc(100vw-5rem))] p-0">
          <Command>
            <CommandInput placeholder="Search genies..." className="text-sm" />
            <CommandList className="max-h-80">
              <CommandEmpty>No genies found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="shared library only"
                  onSelect={() => handleSelect("none")}
                  className="flex cursor-pointer items-center gap-3"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#4E9CFF] to-[#FF4DB8] text-white">
                    <LibraryBig className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-gray-900">Shared library only</div>
                    <div className="text-xs text-gray-500">Connect it to one or more Genies later</div>
                  </div>
                  {selectedAgentId === "none" && <Check className="h-4 w-4 text-genie-primary" />}
                </CommandItem>
              </CommandGroup>
              <CommandGroup heading="Your Genies">
                {agents.map((agent) => {
                  const brand = getBrandColor(agent);
                  const isSelected = selectedAgentId === agent.id;

                  return (
                    <CommandItem
                      key={agent.id}
                      value={`${agent.name} ${agent.id}`}
                      onSelect={() => handleSelect(agent.id)}
                      className="flex cursor-pointer items-center gap-3"
                    >
                      <span className="flex h-8 w-8 shrink-0 overflow-hidden rounded-full border" style={{ borderColor: `${brand}66` }}>
                        <GenieAvatar
                          size={32}
                          color={brand}
                          secondaryColor={agent.branding?.secondaryColor}
                          gradientEnabled={agent.branding?.gradientEnabled}
                          gradientAngle={agent.branding?.gradientAngle}
                          customAvatarUrl={agent.branding?.logoUrl || agent.branding?.customAvatarUrl}
                          hasCustomBranding={hasCustomBranding(agent)}
                        />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold" style={{ color: isSelected ? brand : "#111827" }}>{agent.name}</div>
                        <div className="truncate text-xs text-gray-500">Add this knowledge to {agent.name}</div>
                      </div>
                      {isSelected && <Check className="h-4 w-4" style={{ color: brand }} />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default AgentSelection;
